(function () {
  'use strict';

  const { ON_CHANGE_DEBOUNCE_TIMER, GalleryCard, ModalProduct, debounce } = window.Universe;
  const gsap = window.gsap;

  /* -- CollectionGridFilters ---------------------------------------- */

  class CollectionGridFilters {
    constructor(filters, collection, updateParent) {
      this.init(filters, collection, updateParent);
    }

    setup() {
      this.setupFiltersUsage();
      this.setupMobileFiltersUsage();

      this.setupFiltersInteractions();
      this.setupSortInteractions();
      this.setupMobileFiltersInteractions();

      this.setupRemovers();
    }

    setupRemovers() {
      const activeFiltersContainer = this.DOM.filters.querySelector('.js-active-filters');
      if (!activeFiltersContainer || activeFiltersContainer.dataset.removersBound) return;

      /* Delegate: fetchURL swaps this container's innerHTML on every update,
         so per-chip listeners would be lost. The container itself persists. */
      activeFiltersContainer.dataset.removersBound = 'true';
      activeFiltersContainer.addEventListener('click', this.removeFilter.bind(this));
    }

    getCleanNode(el) {
      if (!el) return el;
      if (el.hasAttribute('data-bound')) {
        const clone = el.cloneNode(true);
        el.parentNode.replaceChild(clone, el);
        return clone;
      }
      el.setAttribute('data-bound', '');
      return el;
    }

    setFocusable(list, enabled) {
      const focusableSelectors = 'input, button, select, textarea, a[href], [tabindex]';
      const elements = list.querySelectorAll(focusableSelectors);

      elements.forEach((el) => {
        if (enabled) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', '-1');
        }
      });
    }

    setContainerInteractivity(container, enable) {
      if (!container) return;

      const supportsInert = 'inert' in HTMLElement.prototype;
      container.setAttribute('aria-hidden', enable ? 'false' : 'true');

      if (supportsInert) {
        container.inert = !enable;
      } else {
        const focusableSelectors = 'a, button, input, select, textarea, [tabindex]';
        const focusables = container.querySelectorAll(focusableSelectors);
        focusables.forEach((el) => {
          if (enable) {
            if (el.hasAttribute('data-prev-tabindex')) {
              const prev = el.getAttribute('data-prev-tabindex');
              if (prev === '') el.removeAttribute('tabindex');
              else el.setAttribute('tabindex', prev);
              el.removeAttribute('data-prev-tabindex');
            } else if (el.getAttribute('tabindex') === '-1') {
              el.removeAttribute('tabindex');
            }
          } else {
            if (!el.hasAttribute('data-prev-tabindex')) {
              el.setAttribute('data-prev-tabindex', el.getAttribute('tabindex') || '');
            }
            el.setAttribute('tabindex', '-1');
          }
        });
      }
    }

    lockBodyScroll() {
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + this.scrollPosition + 'px';
      document.body.style.width = '100%';

      this.preventTouchmove = (e) => {
        const modalContent = this.mobileFiltersForm?.querySelector('.collection-grid__filters-mobile__form-fieldsets');
        if (modalContent && modalContent.contains(e.target)) {
          return;
        }
        e.preventDefault();
      };

      document.addEventListener('touchmove', this.preventTouchmove, { passive: false });
    }

    unlockBodyScroll() {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';

      if (this.preventTouchmove) {
        document.removeEventListener('touchmove', this.preventTouchmove, { passive: false });
        this.preventTouchmove = undefined;
      }

      if (this.scrollPosition !== undefined) {
        window.scrollTo(0, this.scrollPosition);
        this.scrollPosition = undefined;
      }
    }

    setupMobileFilterButtonVisibility(mobileFiltersBtn) {
      const sentinel = document.createElement('div');
      sentinel.classList.add('collection-grid__sentinel');
      sentinel.style.position = 'absolute';
      sentinel.style.bottom = '0';
      sentinel.style.left = '0';
      sentinel.style.width = '100%';
      sentinel.style.height = '1px';
      sentinel.style.pointerEvents = 'none';

      this.DOM.collection.style.position = 'relative';
      this.DOM.collection.appendChild(sentinel);

      this.mobileBtnSentinel = sentinel;

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      };

      this.mobileBtnObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            mobileFiltersBtn.style.opacity = '0';
            setTimeout(() => {
              if (mobileFiltersBtn.style.opacity === '0') {
                mobileFiltersBtn.style.display = 'none';
              }
            }, 300);
          } else if (entry.boundingClientRect.top > 0) {
            mobileFiltersBtn.style.display = 'flex';
            mobileFiltersBtn.style.opacity = '1';
          }
        });
      }, options);

      this.mobileBtnObserver.observe(sentinel);
    }

    setupFiltersInteractions() {
      const filtersBtn = this.DOM.filters.querySelector('.js-filters-button');
      const filtersContainer = this.DOM.collection.querySelector('.js-filters-container');

      if (filtersBtn && filtersContainer) {
        const newFiltersBtn = this.getCleanNode(filtersBtn);

        const isDrawer = filtersContainer.classList.contains('collection-grid__filters-lists__container--drawer');
        const isSidebar = filtersContainer.classList.contains('collection-grid__filters-lists__container--sidebar');

        if (isSidebar) {
          newFiltersBtn.style.display = 'none';
        } else if (isDrawer) {
          this.setContainerInteractivity(filtersContainer, false);
          newFiltersBtn.setAttribute('aria-expanded', 'false');
          newFiltersBtn.setAttribute('aria-controls', 'filters-drawer-' + (this.DOM.collection.dataset.id || 'main'));
          filtersContainer.id = 'filters-drawer-' + (this.DOM.collection.dataset.id || 'main');

          const toggleFilters = (e) => {
            e.preventDefault();
            const isOpen = filtersContainer.classList.toggle('collection-grid__filters-lists--open');
            newFiltersBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            this.setContainerInteractivity(filtersContainer, isOpen);

            if (isOpen) {
              const firstFocusable = filtersContainer.querySelector(
                'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])',
              );
              if (firstFocusable) firstFocusable.focus();
            }
          };

          newFiltersBtn.addEventListener('click', toggleFilters);
        }

        const filterAccordions = this.DOM.collection.querySelectorAll('.js-filter-accordion');
        this.filterAccordionTimelines = [];

        filterAccordions?.forEach((accordion) => {
          const btn = accordion.querySelector('.js-filter-accordion-button');
          const list = accordion.querySelector('.js-filter-accordion-list');

          if (btn && list) {
            const newBtn = this.getCleanNode(btn);

            const tl = gsap.timeline({ reversed: true, paused: true }).to(list, {
              height: 'auto',
              duration: 0.6,
              ease: 'power4.inOut',
            });

            this.filterAccordionTimelines.push(tl);

            const toggleFilter = (e) => {
              e.preventDefault();

              if (tl.reversed()) {
                tl.play();
                newBtn.setAttribute('aria-expanded', 'true');
                this.setFocusable(list, true);
              } else {
                tl.reverse();
                newBtn.setAttribute('aria-expanded', 'false');
                this.setFocusable(list, false);
              }

              const icon = newBtn.querySelector('.collection-grid__filters-list__header-icon svg');
              if (icon) {
                gsap.to(icon, {
                  rotation: tl.reversed() ? 0 : 180,
                  duration: 0.6,
                  ease: 'power4.inOut',
                });
              }
            };

            newBtn.addEventListener('click', toggleFilter);

            newBtn.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleFilter(e);
              }
            });

            list.addEventListener('keydown', (e) => {
              if (e.key === 'Escape' && !tl.reversed()) {
                e.preventDefault();
                tl.reverse();
                newBtn.setAttribute('aria-expanded', 'false');
                this.setFocusable(list, false);
                newBtn.focus();

                const icon = newBtn.querySelector('.collection-grid__filters-list__header-icon svg');
                if (icon) {
                  gsap.to(icon, {
                    rotation: 0,
                    duration: 0.6,
                    ease: 'power4.inOut',
                  });
                }
              }
            });

            const focusableElements = list.querySelectorAll('input, button, select, textarea, a[href]');
            if (focusableElements.length > 0) {
              const firstElement = focusableElements[0];
              const lastElement = focusableElements[focusableElements.length - 1];

              firstElement.addEventListener('keydown', (e) => {
                if (e.shiftKey && e.key === 'Tab' && !tl.reversed()) {
                  e.preventDefault();
                  tl.reverse();
                  newBtn.setAttribute('aria-expanded', 'false');
                  this.setFocusable(list, false);
                  newBtn.focus();

                  const icon = newBtn.querySelector('.collection-grid__filters-list__header-icon svg');
                  if (icon) {
                    gsap.to(icon, {
                      rotation: 0,
                      duration: 0.6,
                      ease: 'power4.inOut',
                    });
                  }
                }
              });

              lastElement.addEventListener('keydown', (e) => {
                if (!e.shiftKey && e.key === 'Tab' && !tl.reversed()) {
                  tl.reverse();
                  newBtn.setAttribute('aria-expanded', 'false');
                  this.setFocusable(list, false);

                  const icon = newBtn.querySelector('.collection-grid__filters-list__header-icon svg');
                  if (icon) {
                    gsap.to(icon, {
                      rotation: 0,
                      duration: 0.6,
                      ease: 'power4.inOut',
                    });
                  }
                }
              });
            }

            if (list.classList.contains('collection-grid__filters-list--open')) {
              tl.play();
              this.setFocusable(list, true);
            } else {
              this.setFocusable(list, false);
            }
          }
        });

        this.priceRangesMin = this.DOM.collection.querySelectorAll('.js-filter-price-range-min');
        this.priceRangesMax = this.DOM.collection.querySelectorAll('.js-filter-price-range-max');
        const priceRanges = this.DOM.collection.querySelectorAll('.js-filter-price-range');
        const minPriceInputs = this.DOM.collection.querySelectorAll('.js-filter-price-value-min');
        const maxPriceInputs = this.DOM.collection.querySelectorAll('.js-filter-price-value-max');

        if (
          priceRanges &&
          this.priceRangesMin &&
          this.priceRangesMax &&
          minPriceInputs.length > 0 &&
          maxPriceInputs.length > 0
        ) {
          const currency = minPriceInputs[0].getAttribute('data-currency');

          const formatPrice = (price) => {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: currency || 'USD',
            }).format(price);
          };

          const parsePrice = (priceString) => {
            const cleaned = priceString.replace(/[^0-9.,]/g, '').replace(',', '.');
            return parseFloat(cleaned) || 0;
          };

          const getPercent = (v, sliderMin, sliderMax) => {
            return Math.round(((v - sliderMin) / (sliderMax - sliderMin)) * 100);
          };

          const updatePriceRange = (index, updateTextInputs = true) => {
            const sliderMin = this.priceRangesMin[index];
            const sliderMax = this.priceRangesMax[index];
            const priceRange = priceRanges[index];

            const rangeMin = parseFloat(sliderMin.getAttribute('min'));
            const rangeMax = parseFloat(sliderMax.getAttribute('max'));
            const minValue = parseFloat(sliderMin.value);
            const maxValue = parseFloat(sliderMax.value);

            const minPercent = getPercent(minValue, rangeMin, rangeMax);
            const maxPercent = getPercent(maxValue, rangeMin, rangeMax);

            priceRange.style.left = minPercent + '%';
            priceRange.style.width = maxPercent - minPercent + '%';

            if (updateTextInputs) {
              minPriceInputs[index].value = formatPrice(minValue);
              maxPriceInputs[index].value = formatPrice(maxValue);
            }
          };

          /* Expose for syncFilterPanels() to reposition sliders after an AJAX update. */
          this.updatePriceRange = updatePriceRange;

          const handleSliderMinChange = (e, index) => {
            const sliderMax = this.priceRangesMax[index];
            const maxValue = parseFloat(sliderMax.value);
            const newValue = Math.min(Number(e.target.value), maxValue - 1);
            this.priceRangesMin[index].value = newValue;
            updatePriceRange(index);
          };

          const handleSliderMaxChange = (e, index) => {
            const sliderMin = this.priceRangesMin[index];
            const minValue = parseFloat(sliderMin.value);
            const newValue = Math.max(Number(e.target.value), minValue + 1);
            this.priceRangesMax[index].value = newValue;
            updatePriceRange(index);
          };

          const handleTextMinChange = (e, index) => {
            const sliderMin = this.priceRangesMin[index];
            const sliderMax = this.priceRangesMax[index];
            const rangeMin = parseFloat(sliderMin.getAttribute('min'));
            const maxValue = parseFloat(sliderMax.value);

            let newValue = parsePrice(e.target.value);
            newValue = Math.max(rangeMin, Math.min(newValue, maxValue - 1));

            sliderMin.value = newValue;
            updatePriceRange(index, false);
          };

          const handleTextMaxChange = (e, index) => {
            const sliderMin = this.priceRangesMin[index];
            const sliderMax = this.priceRangesMax[index];
            const rangeMax = parseFloat(sliderMax.getAttribute('max'));
            const minValue = parseFloat(sliderMin.value);

            let newValue = parsePrice(e.target.value);
            newValue = Math.min(rangeMax, Math.max(newValue, minValue + 1));

            sliderMax.value = newValue;
            updatePriceRange(index, false);
          };

          const handleTextBlur = (e, index) => {
            updatePriceRange(index, true);
          };

          priceRanges.forEach((priceRange, i) => {
            this.priceRangesMin[i].addEventListener('input', (e) => {
              handleSliderMinChange(e, i);
            });
            this.priceRangesMax[i].addEventListener('input', (e) => {
              handleSliderMaxChange(e, i);
            });

            minPriceInputs[i].addEventListener('input', (e) => {
              handleTextMinChange(e, i);
            });
            maxPriceInputs[i].addEventListener('input', (e) => {
              handleTextMaxChange(e, i);
            });
            minPriceInputs[i].addEventListener('blur', (e) => {
              handleTextBlur(e, i);
            });
            maxPriceInputs[i].addEventListener('blur', (e) => {
              handleTextBlur(e, i);
            });

            updatePriceRange(i);
          });
        }
      }

      const gridNarrowBtn = this.DOM.filters.querySelector('.js-filter-grid-narrow');
      const gridLargeBtn = this.DOM.filters.querySelector('.js-filter-grid-large');
      if (gridNarrowBtn && gridLargeBtn) {
        const setGridNarrow = () => {
          this.DOM.collection.classList.remove('collection-grid__product--large');
          this.DOM.collection.classList.add('collection-grid__product--narrow');
        };

        const setGridLarge = () => {
          this.DOM.collection.classList.remove('collection-grid__product--narrow');
          this.DOM.collection.classList.add('collection-grid__product--large');
        };

        gridNarrowBtn.addEventListener('click', setGridNarrow);
        gridLargeBtn.addEventListener('click', setGridLarge);
      }
    }

    setupMobileFiltersInteractions() {
      const subcategoryImages = this.DOM.filters.querySelectorAll('.collection-grid__filters-subcollection__image');
      subcategoryImages.forEach((image) => {
        image.addEventListener('click', (e) => {
          e.preventDefault();
          const container = image.closest('.collection-grid__filters-subcollection__container');
          const link = container?.querySelector('a');
          if (link) {
            window.location.href = link.href;
          }
        });
      });

      const mobileFiltersBtn = this.DOM.filters.querySelector('.js-filters-mobile-button');
      if (mobileFiltersBtn && this.mobileFiltersForm) {
        const wrapper = this.mobileFiltersForm.querySelector('.collection-grid__filters-mobile__form-wrapper');

        const newMobileBtn = this.getCleanNode(mobileFiltersBtn);

        const closeMobileFilters = (wrapperEl, mobileContainer) => {
          gsap
            .timeline({
              onComplete: () => {
                this.mobileFiltersForm.classList.remove('collection-grid__filters-mobile__form--open');
                this.mobileFiltersForm.setAttribute('hidden', 'true');
                this.mobileFiltersForm.setAttribute('aria-expanded', 'false');

                const focusableSelectors = 'button, input, select, textarea, a[href]';
                const allFocusable = this.mobileFiltersForm.querySelectorAll(focusableSelectors);
                allFocusable.forEach((el) => {
                  if (!el.classList.contains('js-filters-mobile-close')) {
                    el.setAttribute('tabindex', '-1');
                  }
                });
              },
            })
            .to(wrapperEl, { y: '100%', duration: 0.8, ease: 'power2.out' }, 0)
            .to(this.mobileFiltersForm, { backgroundColor: 'rgba(0, 0, 0, 0)', duration: 0.7, ease: 'power2.out' }, 0);

          if (mobileContainer) {
            this.unlockBodyScroll();
            newMobileBtn.style.display = 'flex';
            newMobileBtn.style.opacity = '1';
          }
        };

        const toggleFilters = (e) => {
          e.preventDefault();

          const isOpening = !this.mobileFiltersForm.classList.contains('collection-grid__filters-mobile__form--open');
          const currentWrapper = this.mobileFiltersForm.querySelector('.collection-grid__filters-mobile__form-wrapper');
          const mobileContainer = this.mobileFiltersForm.closest('.collection-grid__filters-mobile');

          if (isOpening) {
            this.mobileFiltersForm.classList.add('collection-grid__filters-mobile__form--open');
            this.mobileFiltersForm.removeAttribute('hidden');
            this.mobileFiltersForm.setAttribute('aria-expanded', 'true');

            const focusableSelectors = 'button, input, select, textarea, a[href]';
            const allFocusable = this.mobileFiltersForm.querySelectorAll(focusableSelectors);
            allFocusable.forEach((el) => {
              if (!el.classList.contains('js-filters-mobile-close') && !el.closest('.js-filter-accordion-list')) {
                el.removeAttribute('tabindex');
              }
            });

            const mobileSortForm = this.mobileFiltersForm.querySelector('.js-mobile-filters-sort');
            if (mobileSortForm) {
              const sortItems = mobileSortForm.querySelectorAll('.js-filters-sort-el');
              sortItems.forEach((item) => {
                item.setAttribute('tabindex', '-1');
              });
            }

            if (mobileContainer) {
              newMobileBtn.style.display = 'none';
              this.lockBodyScroll();
            }

            gsap
              .timeline()
              .to(
                this.mobileFiltersForm,
                {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  duration: 0.3,
                  ease: 'power2.out',
                },
                0,
              )
              .to(
                currentWrapper,
                {
                  y: 0,
                  duration: 0.4,
                  ease: 'power2.out',
                  onComplete: () => {
                    const firstFocusable = currentWrapper.querySelector(
                      'button:not([disabled]), input:not([disabled]), select:not([disabled])',
                    );
                    firstFocusable?.focus();
                  },
                },
                0,
              );
          } else {
            closeMobileFilters(currentWrapper, mobileContainer);
          }
        };

        newMobileBtn.addEventListener('click', toggleFilters);

        if (!this.mobileFiltersForm.classList.contains('collection-grid__filters-mobile__form--open')) {
          newMobileBtn.style.display = 'flex';
          newMobileBtn.style.opacity = '1';
        }

        const mobileSortBtn = this.mobileFiltersForm.querySelector('.js-filters-sort-button');
        if (mobileSortBtn) {
          const newMobileSortBtn = this.getCleanNode(mobileSortBtn);

          const mobileSortForm = this.mobileFiltersForm.querySelector('.js-mobile-filters-sort');

          if (mobileSortForm) {
            const sortTl = gsap.timeline({ reversed: true, paused: true }).to(
              mobileSortForm,
              {
                height: 'auto',
                opacity: 1,
                duration: 0.6,
                ease: 'power4.inOut',
              },
              0,
            );

            this.mobileSortTimeline = sortTl;

            newMobileSortBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();

              if (sortTl.reversed()) {
                sortTl.play();

                const sortItems = mobileSortForm.querySelectorAll('.js-filters-sort-el');
                sortItems.forEach((item) => {
                  item.removeAttribute('tabindex');
                });
              } else {
                sortTl.reverse();

                const sortItems = mobileSortForm.querySelectorAll('.js-filters-sort-el');
                sortItems.forEach((item) => {
                  item.setAttribute('tabindex', '-1');
                });
              }

              const icon = newMobileSortBtn.querySelector('.button__icon svg');
              if (icon) {
                gsap.to(icon, {
                  rotation: sortTl.reversed() ? 0 : 180,
                  duration: 0.6,
                  ease: 'power4.inOut',
                });
              }
            });
          }

          const mobileSortLabels = this.mobileFiltersForm.querySelectorAll('.js-filters-sort-el');
          mobileSortLabels.forEach((label) => {
            label.addEventListener('click', (e) => {
              const sortName = label.dataset.sortName;

              if (newMobileSortBtn && newMobileSortBtn.querySelector('p')) {
                newMobileSortBtn.querySelector('p').textContent = sortName;
              }

              if (mobileSortForm && this.mobileSortTimeline) {
                this.mobileSortTimeline.reverse();

                const sortItems = mobileSortForm.querySelectorAll('.js-filters-sort-el');
                sortItems.forEach((item) => {
                  item.setAttribute('tabindex', '-1');
                });

                const icon = newMobileSortBtn.querySelector('.button__icon svg');
                if (icon) {
                  gsap.to(icon, {
                    rotation: 0,
                    duration: 0.6,
                    ease: 'power4.inOut',
                  });
                }
              }
            });
          });
        }

        const handleClickOutside = (e) => {
          const isModalOpen = this.mobileFiltersForm.classList.contains('collection-grid__filters-mobile__form--open');
          if (!isModalOpen) return;

          const formWrapper = this.mobileFiltersForm.querySelector('.collection-grid__filters-mobile__form-wrapper');
          const isClickInsideModal = formWrapper && formWrapper.contains(e.target);
          const isClickOnButton = newMobileBtn.contains(e.target);

          if (!isClickInsideModal && !isClickOnButton) {
            const mobileContainer = this.mobileFiltersForm.closest('.collection-grid__filters-mobile');
            closeMobileFilters(formWrapper, mobileContainer);
          }
        };

        document.removeEventListener('click', this.handleClickOutside);
        this.handleClickOutside = handleClickOutside;
        document.addEventListener('click', this.handleClickOutside);

        const handleEscapeKey = (e) => {
          if (
            e.key === 'Escape' &&
            this.mobileFiltersForm.classList.contains('collection-grid__filters-mobile__form--open')
          ) {
            e.preventDefault();
            const escWrapper = this.mobileFiltersForm.querySelector('.collection-grid__filters-mobile__form-wrapper');
            const mobileContainer = this.mobileFiltersForm.closest('.collection-grid__filters-mobile');
            closeMobileFilters(escWrapper, mobileContainer);
          }
        };

        document.removeEventListener('keydown', this.handleEscapeKey);
        this.handleEscapeKey = handleEscapeKey;
        document.addEventListener('keydown', this.handleEscapeKey);

        const closeBtn = this.mobileFiltersForm.querySelector('.js-filters-mobile-close');
        if (closeBtn) {
          const newCloseBtn = this.getCleanNode(closeBtn);

          const handleCloseClick = (e) => {
            e.preventDefault();
            const closeWrapper = this.mobileFiltersForm.querySelector('.collection-grid__filters-mobile__form-wrapper');
            const mobileContainer = this.mobileFiltersForm.closest('.collection-grid__filters-mobile');
            closeMobileFilters(closeWrapper, mobileContainer);
          };

          newCloseBtn.addEventListener('click', handleCloseClick);
        }

        const mobileFilterAccordions = this.mobileFiltersForm.querySelectorAll('.js-filter-accordion');
        this.mobileFilterAccordionTimelines = [];

        mobileFilterAccordions?.forEach((accordion) => {
          const btn = accordion.querySelector('.js-filter-accordion-button');
          const list = accordion.querySelector('.js-filter-accordion-list');

          if (btn && list) {
            const newBtn = this.getCleanNode(btn);

            const tl = gsap.timeline({ reversed: true, paused: true }).to(list, {
              height: 'auto',
              duration: 0.6,
              ease: 'power4.inOut',
            });

            this.mobileFilterAccordionTimelines.push(tl);

            const toggleFilter = (e) => {
              e.preventDefault();

              if (tl.reversed()) {
                tl.play();
                newBtn.setAttribute('aria-expanded', 'true');
                this.setFocusable(list, true);
              } else {
                tl.reverse();
                newBtn.setAttribute('aria-expanded', 'false');
                this.setFocusable(list, false);
              }

              const icon = newBtn.querySelector('.collection-grid__filters-list__header-icon svg');
              if (icon) {
                gsap.to(icon, {
                  rotation: tl.reversed() ? 0 : 180,
                  duration: 0.6,
                  ease: 'power4.inOut',
                });
              }
            };

            newBtn.addEventListener('click', toggleFilter);

            newBtn.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleFilter(e);
              }
            });

            list.addEventListener('keydown', (e) => {
              if (e.key === 'Escape' && !tl.reversed()) {
                e.preventDefault();
                tl.reverse();
                newBtn.setAttribute('aria-expanded', 'false');
                this.setFocusable(list, false);
                newBtn.focus();

                const icon = newBtn.querySelector('.collection-grid__filters-list__header-icon svg');
                if (icon) {
                  gsap.to(icon, {
                    rotation: 0,
                    duration: 0.6,
                    ease: 'power4.inOut',
                  });
                }
              }
            });

            const focusableElements = list.querySelectorAll('input, button, select, textarea, a[href]');
            if (focusableElements.length > 0) {
              const firstElement = focusableElements[0];
              const lastElement = focusableElements[focusableElements.length - 1];

              firstElement.addEventListener('keydown', (e) => {
                if (e.shiftKey && e.key === 'Tab' && !tl.reversed()) {
                  e.preventDefault();
                  tl.reverse();
                  newBtn.setAttribute('aria-expanded', 'false');
                  this.setFocusable(list, false);
                  newBtn.focus();

                  const icon = newBtn.querySelector('.collection-grid__filters-list__header-icon svg');
                  if (icon) {
                    gsap.to(icon, {
                      rotation: 0,
                      duration: 0.6,
                      ease: 'power4.inOut',
                    });
                  }
                }
              });

              lastElement.addEventListener('keydown', (e) => {
                if (!e.shiftKey && e.key === 'Tab' && !tl.reversed()) {
                  tl.reverse();
                  newBtn.setAttribute('aria-expanded', 'false');
                  this.setFocusable(list, false);

                  const icon = newBtn.querySelector('.collection-grid__filters-list__header-icon svg');
                  if (icon) {
                    gsap.to(icon, {
                      rotation: 0,
                      duration: 0.6,
                      ease: 'power4.inOut',
                    });
                  }
                }
              });
            }

            if (list.classList.contains('collection-grid__filters-list--open')) {
              tl.play();
              this.setFocusable(list, true);
            } else {
              this.setFocusable(list, false);
            }
          }
        });

        this.setupMobileFilterButtonVisibility(newMobileBtn);
      }
    }

    setupFiltersUsage() {
      this.mainFiltersForm = this.DOM.collection.querySelector('.js-filters-container');
      if (this.mainFiltersForm) {
        this.mainFiltersForm.addEventListener(
          'input',
          debounce(this.onFilterInputSubmit.bind(this), ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    }

    setupSortInteractions() {
      this.sortForms = this.DOM.filters.querySelectorAll('.js-filters-sort');
      this.sortFormBtns = this.DOM.filters.querySelectorAll('.js-filters-sort-button');

      if (this.sortForms && this.sortFormBtns) {
        this.sortFormBtns.forEach((btn, i) => {
          const form = this.sortForms[i];
          const options = form.querySelectorAll('.js-filters-sort-el');

          const updateOptionsTabindex = (isOpen) => {
            options.forEach((option) => {
              if (isOpen) {
                option.setAttribute('tabindex', '0');
              } else {
                option.setAttribute('tabindex', '-1');
              }
            });
          };

          const toggleSorts = (e) => {
            e.preventDefault();

            btn.classList.toggle('collection-grid__filters-sort__button--open');
            form.classList.toggle('collection-grid__filters-sort--open');

            const isExpanded = btn.classList.contains('collection-grid__filters-sort__button--open');
            btn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

            updateOptionsTabindex(isExpanded);

            if (isExpanded) {
              const firstOption = form.querySelector('input:checked')?.closest('.js-filters-sort-el') || options[0];
              if (firstOption) {
                firstOption.focus();
              }
            }
          };

          options.forEach((option) => {
            const radio = option.querySelector('input[type="radio"]');
            option.setAttribute('role', 'option');
            option.setAttribute('tabindex', '-1');
            if (radio?.checked) {
              option.setAttribute('aria-selected', 'true');
            } else {
              option.setAttribute('aria-selected', 'false');
            }
          });

          const handleButtonKeydown = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleSorts(e);
            }
          };

          const handleEscapeKey = (e) => {
            if (e.key === 'Escape' && btn.classList.contains('collection-grid__filters-sort__button--open')) {
              e.preventDefault();
              btn.classList.remove('collection-grid__filters-sort__button--open');
              form.classList.remove('collection-grid__filters-sort--open');
              btn.setAttribute('aria-expanded', 'false');
              updateOptionsTabindex(false);
              btn.focus();
            }
          };

          const handleOptionKeydown = (e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();

              if (!btn.classList.contains('collection-grid__filters-sort__button--open')) {
                toggleSorts(e);
                return;
              }

              const currentFocusedIndex = Array.from(options).findIndex((opt) => opt === document.activeElement);
              let nextIndex = currentFocusedIndex;

              if (e.key === 'ArrowDown') {
                nextIndex = (currentFocusedIndex + 1) % options.length;
              } else if (e.key === 'ArrowUp') {
                nextIndex = (currentFocusedIndex - 1 + options.length) % options.length;
              }

              options[nextIndex].focus();
            } else if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const radio = e.currentTarget.querySelector('input[type="radio"]');
              if (radio) {
                radio.click();
              }
            } else if (e.key === 'Tab') {
              const isShiftTab = e.shiftKey;
              const currentFocusedIndex = Array.from(options).findIndex((opt) => opt === document.activeElement);
              let nextIndex;

              if (isShiftTab) {
                nextIndex = currentFocusedIndex - 1;
                if (nextIndex < 0) {
                  e.preventDefault();
                  btn.classList.remove('collection-grid__filters-sort__button--open');
                  form.classList.remove('collection-grid__filters-sort--open');
                  btn.setAttribute('aria-expanded', 'false');
                  updateOptionsTabindex(false);
                  btn.focus();
                } else {
                  e.preventDefault();
                  options[nextIndex].focus();
                }
              } else {
                nextIndex = currentFocusedIndex + 1;
                if (nextIndex >= options.length) {
                  btn.classList.remove('collection-grid__filters-sort__button--open');
                  form.classList.remove('collection-grid__filters-sort--open');
                  btn.setAttribute('aria-expanded', 'false');
                  updateOptionsTabindex(false);
                } else {
                  e.preventDefault();
                  options[nextIndex].focus();
                }
              }
            }
          };

          btn.addEventListener('click', toggleSorts);
          btn.addEventListener('keydown', handleButtonKeydown);
          btn.addEventListener('keydown', handleEscapeKey);

          options.forEach((option) => {
            option.addEventListener('keydown', handleOptionKeydown);
            option.addEventListener('keydown', handleEscapeKey);
            option.addEventListener('click', () => {
              btn.classList.remove('collection-grid__filters-sort__button--open');
              form.classList.remove('collection-grid__filters-sort--open');
              btn.setAttribute('aria-expanded', 'false');
              updateOptionsTabindex(false);
              btn.focus();
            });
          });
        });

        this.sortForms.forEach((form) => {
          form.addEventListener('input', (e) => {
            const formOptions = form.querySelectorAll('.js-filters-sort-el');

            formOptions.forEach((option) => {
              const radio = option.querySelector('input[type="radio"]');
              if (radio?.checked) {
                option.setAttribute('aria-selected', 'true');
              } else {
                option.setAttribute('aria-selected', 'false');
              }
            });

            debounce(this.onSortFilterInputSubmit.bind(this), ON_CHANGE_DEBOUNCE_TIMER)(e);
          });
        });
      }
    }

    setupMobileFiltersUsage() {
      this.selectedMobileSortValue = undefined;

      this.mobileFiltersForm = this.DOM.filters.querySelector('.js-filters-mobile-form');
      this.mobileFiltersFormSubmit = this.DOM.filters.querySelector('.js-filters-mobile-submit');
      this.mobileFiltersFormSubmitCounter = this.DOM.filters.querySelector('.js-filters-mobile-submit-counter');

      if (this.mobileFiltersForm && this.mobileFiltersFormSubmit && this.mobileFiltersFormSubmitCounter) {
        this.mobileFiltersForm = this.getCleanNode(this.mobileFiltersForm);
        this.mobileFiltersFormSubmit = this.DOM.filters.querySelector('.js-filters-mobile-submit');
        this.mobileFiltersFormSubmitCounter = this.DOM.filters.querySelector('.js-filters-mobile-submit-counter');

        this.mobileFiltersForm.addEventListener(
          'input',
          debounce(this.onMobileFilterInputChange.bind(this), ON_CHANGE_DEBOUNCE_TIMER),
        );

        this.mobileFiltersForm.addEventListener('submit', this.onMobileFilterInputSubmit.bind(this));
      }
    }

    createFormData(event) {
      return new FormData(event.target.closest('form'));
    }

    createSearchParams(event) {
      const formData = this.createFormData(event);
      return new URLSearchParams(formData).toString();
    }

    createURL(params) {
      const sectionID = this.DOM.collection.dataset.id;
      return window.location.pathname + '?section_id=' + sectionID + '&' + params;
    }

    updateURL(url) {
      const urlCopy = new URL(url, window.location);
      urlCopy.searchParams.delete('section_id');
      history.replaceState({}, '', urlCopy.toString());
    }

    fetchURL(params) {
      const url = this.createURL(params);

      return fetch(url)
        .then((response) => {
          if (!response.ok) {
            console.log(response);
            throw new Error('HTTP error! status: ' + response.status);
          }
          return response.text();
        })
        .then((responseText) => {
          const doc = new DOMParser().parseFromString(responseText, 'text/html');
          const newHTML = doc.querySelector('.js-grid');
          const newFilters = doc.querySelector('.js-filters');

          /* NOTE: innerHTML assignments below are intentional - content comes from same-origin Shopify section rendering */
          if (newHTML) {
            const newProductGallery = newHTML.querySelector('.collection-grid__product-gallery');
            const currentProductGallery = this.DOM.collection.querySelector('.collection-grid__product-gallery');

            if (newProductGallery && currentProductGallery) {
              currentProductGallery.innerHTML = newProductGallery.innerHTML;
            }

            const newHiddenFilters = newHTML.querySelector('.js-hidden-inputs');
            const currentHiddenFilters = this.DOM.collection.querySelector('.js-hidden-inputs');
            if (newHiddenFilters && currentHiddenFilters) {
              currentHiddenFilters.innerHTML = newHiddenFilters.innerHTML;
            }
          }

          if (newFilters) {
            const newActiveFilters = newFilters.querySelector('.js-active-filters');
            const currentActiveFilters = this.DOM.collection.querySelector('.js-active-filters');
            if (newActiveFilters && currentActiveFilters) {
              currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
            }

            const newFilterCounter = newFilters.querySelector('.collection-grid__filters-results');
            const currentFilterCounter = this.DOM.collection.querySelector('.collection-grid__filters-results');
            if (newFilterCounter && currentFilterCounter) {
              currentFilterCounter.innerHTML = newFilterCounter.innerHTML;
            }
          }

          this.syncFilterPanels(doc);
          this.updateURL(url);
          this.updateParent();
        })
        .catch((e) => {
          throw e;
        });
    }

    syncFilterPanels(doc) {
      /* Reconcile filter panel state from the freshly-rendered section without
         replacing the panels — preserves accordion/GSAP state, focus, and the
         existing input listeners. Needed so removing a chip (or clear-all)
         updates the underlying checkboxes/sliders, which fetchURL never touches. */
      const checkboxSelector =
        '.js-filters-container input[type="checkbox"], .js-filters-mobile-form input[type="checkbox"]';

      const checkedMap = new Map();
      doc.querySelectorAll(checkboxSelector).forEach((cb) => {
        checkedMap.set(cb.name + '|' + cb.value, cb.checked);
      });

      this.DOM.collection.querySelectorAll(checkboxSelector).forEach((cb) => {
        const key = cb.name + '|' + cb.value;
        if (checkedMap.has(key)) cb.checked = checkedMap.get(key);
      });

      if (this.priceRangesMin && this.priceRangesMax && typeof this.updatePriceRange === 'function') {
        const newMin = doc.querySelectorAll('.js-filter-price-range-min');
        const newMax = doc.querySelectorAll('.js-filter-price-range-max');

        this.priceRangesMin.forEach((slider, i) => {
          if (newMin[i]) slider.value = newMin[i].value;
          if (newMax[i] && this.priceRangesMax[i]) this.priceRangesMax[i].value = newMax[i].value;
          this.updatePriceRange(i, true);
        });
      }

      this.updateMobileSubmitCounter();
    }

    onFilterInputSubmit(event) {
      event.preventDefault();
      const mainFiltersFormSearchParams = this.createSearchParams(event);
      this.fetchURL(mainFiltersFormSearchParams);
    }

    onSortFilterInputSubmit(event) {
      const target = event.target;
      const sortForm = target.closest('.js-filters-sort');
      const isMobile = sortForm && sortForm.classList.contains('js-mobile-filters-sort');

      if (isMobile) {
        return;
      }

      event.preventDefault();

      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);

      const sortFormSearchParams = url.searchParams.toString();

      this.fetchURL(sortFormSearchParams).then(() => {
        const activeSort = target.closest('.js-filters-sort-el:has(input:checked)');
        const sortValue = activeSort.dataset.sortValue;
        const sortName = activeSort.dataset.sortName;

        this.sortForms.forEach((form) => {
          const sorts = form.querySelectorAll('input');
          sorts.forEach((sort) => {
            if (sort.value === target.value) sort.checked = true;
            else sort.checked = false;
          });
          form.classList.remove('collection-grid__filters-sort--open');
        });

        this.sortFormBtns.forEach((btn, index) => {
          if (btn && btn.querySelector('.button__text')) {
            btn.querySelector('.button__text').textContent = sortName;
            btn.classList.remove('collection-grid__filters-sort__button--open');
          }

          if (isMobile && this.sortForms[index]) {
            this.sortForms[index].classList.remove('collection-grid__filters-sort--open');
          }
        });
      });
    }

    removeFilter(event) {
      const filterBtn = event.target.closest('.js-active-filter');
      if (!filterBtn) return;

      event.preventDefault();
      const url = new URL(filterBtn.href, window.location);
      const params = url.searchParams.toString();
      this.fetchURL(params);
    }

    countMobileActiveFilters(formData) {
      let activeFilters = 0;

      for (const pair of formData.entries()) {
        const key = pair[0];
        const value = pair[1];
        if (key === 'sort_by') {
          /* skip sort */
        } else if (key === 'filter.v.price.gte') {
          if (this.priceRangesMin && this.priceRangesMin[1]) {
            const priceRangeMinValue = parseFloat(this.priceRangesMin[1].getAttribute('min'));
            const floatValue = parseFloat(value);
            if (floatValue !== priceRangeMinValue) activeFilters++;
          }
        } else if (key === 'filter.v.price.lte') {
          if (this.priceRangesMax && this.priceRangesMax[1]) {
            const priceRangeMaxValue = parseFloat(this.priceRangesMax[1].getAttribute('max'));
            const floatValue = parseFloat(value);
            if (floatValue !== priceRangeMaxValue) activeFilters++;
          }
        } else if (key.includes('filter.') && value) {
          activeFilters++;
        }
      }

      return activeFilters;
    }

    updateMobileSubmitCounter() {
      if (!this.mobileFiltersForm || !this.mobileFiltersFormSubmitCounter) return;

      const activeFilters = this.countMobileActiveFilters(new FormData(this.mobileFiltersForm));
      this.mobileFiltersFormSubmitCounter.textContent = '(' + activeFilters.toString().padStart(2, 0) + ')';
    }

    onMobileFilterInputChange(event) {
      event.preventDefault();

      this.mobileFormData = this.createFormData(event);
      const activeFilters = this.countMobileActiveFilters(this.mobileFormData);

      this.mobileFiltersFormSubmitCounter.textContent = '(' + activeFilters.toString().padStart(2, 0) + ')';
    }

    onMobileFilterInputSubmit(event) {
      event.preventDefault();

      const mobileFiltersFormSearchParams = this.createSearchParams(event);

      const wrapper = this.mobileFiltersForm.querySelector('.collection-grid__filters-mobile__form-wrapper');

      gsap
        .timeline({
          onComplete: () => {
            this.fetchURL(mobileFiltersFormSearchParams).then(() => {
              this.mobileFiltersForm.classList.remove('collection-grid__filters-mobile__form--open');
              this.mobileFiltersForm.setAttribute('hidden', 'true');
              this.mobileFiltersForm.setAttribute('aria-expanded', 'false');

              const focusableSelectors = 'button, input, select, textarea, a[href]';
              const allFocusable = this.mobileFiltersForm.querySelectorAll(focusableSelectors);
              allFocusable.forEach((el) => {
                if (!el.classList.contains('js-filters-mobile-close')) {
                  el.setAttribute('tabindex', '-1');
                }
              });

              const mobileBtn = this.DOM.filters.querySelector('.js-filters-mobile-button');
              if (mobileBtn) {
                mobileBtn.style.display = 'flex';
                mobileBtn.style.opacity = '1';
              }
            });
          },
        })
        .to(
          wrapper,
          {
            y: '100%',
            duration: 0.8,
            ease: 'power2.out',
          },
          0,
        )
        .to(
          this.mobileFiltersForm,
          {
            backgroundColor: 'rgba(0, 0, 0, 0)',
            duration: 0.7,
            ease: 'power2.out',
          },
          0,
        );

      this.unlockBodyScroll();
    }

    resize() {
      if (this.DOM) {
      }
    }

    destroy() {
      if (this.DOM) {
        if (this.mobileBtnObserver) {
          this.mobileBtnObserver.disconnect();
          this.mobileBtnObserver = undefined;
        }

        if (this.mobileBtnSentinel && this.mobileBtnSentinel.parentNode) {
          this.mobileBtnSentinel.parentNode.removeChild(this.mobileBtnSentinel);
          this.mobileBtnSentinel = undefined;
        }

        if (this.preventTouchmove) {
          this.unlockBodyScroll();
        }

        if (this.handleClickOutside) {
          document.removeEventListener('click', this.handleClickOutside);
          this.handleClickOutside = undefined;
        }
      }
    }

    init(filters, collection, updateParent) {
      if (filters && collection && updateParent) {
        this.DOM = {};
        this.DOM.filters = filters;
        this.DOM.collection = collection;

        this.updateParent = updateParent;

        this.setup();
      }
    }
  }

  /* -- CollectionGrid ------------------------------------------------ */

  class CollectionGrid {
    constructor(section) {
      this.init(section);
    }

    setup() {
      this.filters = new CollectionGridFilters(this.DOM.filters, this.DOM.collection, this.update.bind(this));
      this.setupCards();
    }

    setupCards() {
      this.DOM.cards = this.DOM.collection.querySelectorAll('.js-gallery-card');
      this.DOM.quickAdd = document.querySelector('.js-quick-add-container');

      if (this.DOM.cards) {
        this.cards = [];
        this.quickAdds = [];

        this.DOM.cards?.forEach((card) => {
          const cardInstance = new GalleryCard(card);
          this.cards.push(cardInstance);

          if (this.DOM.quickAdd) {
            const cardQuickAdd = new ModalProduct(card, this.DOM.quickAdd);
            this.quickAdds.push(cardQuickAdd);
          }
        });
      }
    }

    destroyCards() {
      this.cards?.forEach((card) => {
        card.destroy();
      });
      this.quickAdds?.forEach((quickAdd) => {
        quickAdd.destroy();
      });

      this.cards = undefined;
      this.quickAdds = undefined;
      this.DOM.cards = undefined;
      this.DOM.quickAdds = undefined;
    }

    resize() {
      if (this.DOM) {
      }
    }

    update() {
      this.destroyCards();
      this.setupCards();
    }

    destroy() {
      if (this.DOM) {
        this.filters?.destroy();
        this.destroyCards();
      }
    }

    init(section) {
      if (section) {
        this.DOM = {};
        this.DOM.collection = section;
        this.DOM.grid = section.querySelector('.js-grid');
        this.DOM.filters = section.querySelector('.js-filters');
        this.sectionId = section.closest('.shopify-section')?.id || section.dataset.id;

        this.setup();
      }
    }
  }

  /* -- CollectionGrids ------------------------------------------------ */

  class CollectionGrids {
    constructor() {
      this.init();
    }

    setup() {
      for (let i = 0; i < this.DOM.sections.length; i++) {
        this.instances.push(new CollectionGrid(this.DOM.sections[i]));
      }
    }

    reinit() {
      this.destroy();
      this.init();
    }

    reinitSection(sectionEl) {
      this.destroySection(sectionEl);
      if (!this.DOM) { this.DOM = {}; this.instances = []; }
      sectionEl.querySelectorAll('.js-collection-grid').forEach((section) => {
        this.instances.push(new CollectionGrid(section));
      });
      this.DOM.sections = document.querySelectorAll('.js-collection-grid');
    }

    destroySection(sectionEl) {
      if (!this.instances) return;
      const targetId = sectionEl.id || sectionEl.closest('.shopify-section')?.id;
      if (!targetId) return;
      for (let i = this.instances.length - 1; i >= 0; i--) {
        if (this.instances[i].sectionId === targetId) {
          this.instances[i].destroy();
          this.instances.splice(i, 1);
        }
      }
    }

    destroy() {
      if (this.DOM) {
        for (let i = 0; i < this.instances.length; i++) {
          this.instances[i].destroy();
        }
        this.instances = [];
        this.DOM = undefined;
      }
    }

    init(container = document) {
      const sections = container.querySelectorAll('.js-collection-grid');

      if (sections.length) {
        this.DOM = {};
        this.DOM.sections = sections;

        this.instances = [];

        this.setup();
      }
    }
  }

  window.Universe = window.Universe || {};
  window.Universe.CollectionGrids = CollectionGrids;
  window.Universe.CollectionGrid = CollectionGrid;
  window.Universe.CollectionGridFilters = CollectionGridFilters;
})();
